'use client';

import React, { useState } from 'react';
import HistoryIcon from '@mui/icons-material/History';
import { Box, Button, Skeleton, Typography } from '@mui/material';

import { useAuth, useT } from '@/hooks';
import { useStreakHistory } from '@/services/query/streak.queries';
import type { StreakPointsTransaction } from '@/types/streak.types';
// Deep import, not the dashboard barrel: that barrel re-exports MetricCard,
// PerformanceChart and PortfolioMobileSummaryCard, which all import recharts —
// pulling ~1 MB of charts into every route that reaches the streaks barrel.
import { DashboardCard } from '@/views/Portfolio/components/dashboard/DashboardCard';

const PAGE_SIZE = 10;

const REASON_LABEL: Record<string, string> = {
  daily_qualify: 'Daily target hit',
  milestone_day7: 'Day 7 bonus',
  freeze_purchase: 'Freeze purchased',
};

function formatReason(reason: string): string {
  return REASON_LABEL[reason] || reason.replace(/_/g, ' ');
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * StreakHistorySection
 * Points ledger — every day you qualified, every bonus, every freeze spend.
 * The API for this (`GET /streak/history`) already existed but had no UI
 * anywhere in the app; this is its first consumer.
 */
export const StreakHistorySection: React.FC = () => {
  const { t } = useT();
  const { isAuthenticated } = useAuth();
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isFetching } = useStreakHistory(
    { limit: PAGE_SIZE, offset },
    isAuthenticated,
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasMore = offset + items.length < total;

  return (
    <DashboardCard
      title={t('streaks.historyTitle', { defaultValue: 'Points history' })}
      subtitle={t('streaks.historySubtitle', { defaultValue: 'Every point earned or spent' })}
      icon={<HistoryIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={44} />
          ))}
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {t('streaks.historyEmpty', {
              defaultValue: "No activity yet — trade today's target to earn your first points.",
            })}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {items.map((item) => (
              <HistoryRow key={item.id} item={item} />
            ))}
          </Box>
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                size="small"
                disabled={isFetching}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {t('streaks.historyLoadMore', { defaultValue: 'Load more' })}
              </Button>
            </Box>
          )}
        </>
      )}
    </DashboardCard>
  );
};

const HistoryRow: React.FC<{ item: StreakPointsTransaction }> = ({ item }) => {
  const isPositive = item.amount > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        px: 1.5,
        py: 1.1,
        borderRadius: 2,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
          {formatReason(item.reason)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {formatDate(item.createdAt)}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '0.9rem',
            color: isPositive ? 'success.main' : 'error.main',
          }}
        >
          {isPositive ? '+' : ''}
          {item.amount}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {item.balanceAfter} pts
        </Typography>
      </Box>
    </Box>
  );
};
